import { useMemo, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { ButtonProps, Button } from '@dynatrace/strato-components/buttons';
import {
  CloseSidebarIcon,
  OpenSidebarIcon,
  XmarkIcon,
} from '@dynatrace/strato-icons';

import { usePageContext } from './hooks/usePageContext.js';
import { usePanelContext } from './hooks/usePanelContext.js';
import {
  pageControlButtonIconCSS,
  pageControlButtonIconFlippedCSS,
} from './Page.sty.js';
import { PagePanelsActions } from './types/page-state.js';
import type { DismissiblePagePanels } from './types/shared-page-types.js';
import { isDev } from '../../core/utils/is-node-environment.js';

/**
 * @public
 */
export type PanelControlButtonProps = {
  /** The action triggered by the button. */
  action?: 'toggle' | 'open' | 'close';
  /** The target panel controlled by the button. */
  target?: DismissiblePagePanels;
} & Omit<ButtonProps<'button'>, 'as' | 'type' | 'loading'>;

/**
 * Use the `PanelControlButton` to get the correct icon for toggling the sidebar / detail view.
 * Depending on where it is rendered and the current state of the panel, the corresponding icon is rendered.
 * You can customize the rendered content by passing any children.
 * @public
 */
export const PanelControlButton = (props: PanelControlButtonProps) => {
  const {
    action: actionProp,
    target: targetProp,
    children: childrenProp,
    onClick,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: PanelControlButtonProps = props;
  const { panelName } = usePanelContext();
  const { dispatch, state, setPanelDismissed, isPanelDismissed } =
    usePageContext();
  const action = actionProp ?? (panelName !== undefined ? 'close' : 'toggle');
  const target = targetProp ?? panelName ?? 'details';
  const isDismissed = isPanelDismissed(target);

  const intl = useIntl();
  const [ariaLabelOpen, ariaLabelClose] = useMemo(() => {
    const detailViewTranslation = intl.formatMessage({
      defaultMessage: 'Detail View',
      id: '0ntbampPIxNnacS8',
      description: 'Panel name translation',
    });
    const sidebarTranslation = intl.formatMessage({
      defaultMessage: 'Sidebar',
      id: 'MYBFw8pxHVA1QTDj',
      description: 'Panel name translation',
    });

    const panelNameTranslation =
      target === 'details' ? detailViewTranslation : sidebarTranslation;

    const ariaLabelOpen = intl.formatMessage(
      {
        defaultMessage: 'Open {panelName}',
        id: '01469lp26TkXjPEz',
        description: 'Label for the panel control button.',
      },
      { panelName: panelNameTranslation },
    );

    const ariaLabelClose = intl.formatMessage(
      {
        defaultMessage: 'Close {panelName}',
        id: 'QsfELKqpB23/KKWM',
        description: 'Label for the panel control button.',
      },
      { panelName: panelNameTranslation },
    );

    return [ariaLabelOpen, ariaLabelClose];
  }, [intl, target]);

  const [children, ariaLabel] = useMemo(() => {
    if (childrenProp !== undefined) {
      return [childrenProp];
    }

    let icon: ReactNode;
    let ariaLabel: string;

    switch (action) {
      case 'open':
        icon = <OpenSidebarIcon />;
        ariaLabel = ariaLabelOpen;
        break;
      case 'close':
        icon = <XmarkIcon />;
        ariaLabel = ariaLabelClose;
        break;
      default: {
        icon = isDismissed ? <OpenSidebarIcon /> : <CloseSidebarIcon />;
        ariaLabel = isDismissed ? ariaLabelOpen : ariaLabelClose;
      }
    }

    return [
      <Button.Prefix>
        <div
          className={
            target === 'details'
              ? pageControlButtonIconFlippedCSS
              : pageControlButtonIconCSS
          }
        >
          {icon}
        </div>
      </Button.Prefix>,
      ariaLabel,
    ];
  }, [
    action,
    ariaLabelClose,
    ariaLabelOpen,
    childrenProp,
    isDismissed,
    target,
  ]);

  return (
    <Button
      aria-label={ariaLabel}
      onClick={(event) => {
        onClick?.(event);

        if (!state[target].controlled) {
          let dismissed: boolean | undefined;
          switch (action) {
            case 'open':
              dismissed = false;
              break;
            case 'close':
              dismissed = true;
              break;
            default:
              dismissed = !isDismissed;
          }

          dispatch({
            type: PagePanelsActions.SETPANEL,
            payload: { name: target, userAction: dismissed ? 'close' : 'open' },
          });
          setPanelDismissed(target, dismissed);
        } else if (onClick === undefined && isDev()) {
          // eslint-disable-next-line no-console
          console.warn(
            "You are using the panel control button with a controlled panel, but didn't provide an 'onClick' callback. Implement the 'onClick' to update the panel state with the panel control button.",
          );
        }
      }}
      className={consumerClassName}
      style={consumerStyle}
      {...remainingProps}
    >
      {children}
    </Button>
  );
};

(
  PanelControlButton as typeof PanelControlButton & { displayName: string }
).displayName = 'Page.PanelControlButton';
